import React, { useState, useEffect } from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import LoadingSpinner from "./LoadingSpinner";
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FirebaseService } from '../services/firebaseService';

interface AllProductsProps {
  t: any;
  isRTL: boolean;
  language: 'fr' | 'en' | 'ar';
  setLanguage: (lang: 'fr' | 'en' | 'ar') => void;
  // Search and filter props to pass to Header
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  viewMode: 'grid' | 'list';
  setViewMode: (mode: 'grid' | 'list') => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
}

const AllProductsPage = styled.div<{ $isRTL: boolean }>`
  min-height: 100vh;
  background: #f8fafc;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  position: relative;
  overflow-x: hidden;
  width: 100%;
  padding-top: 200px; /* Just header height */
  
  @media (max-width: 768px) {
    padding-top: 180px; /* Mobile header height */
  }
`;

const ProductsSection = styled.section`
  width: 100%;
  padding: 1.5rem;
`;

const ProductsContainer = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
`;

const ResultsHeader = styled.div<{ $isRTL: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding: 0 0.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
    align-items: center;
    text-align: center;
  }
`;

const ResultsCount = styled.p<{ $isRTL: boolean }>`
  font-size: 1rem;
  color: #64748b;
  margin: 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const ProductsGrid = styled.div<{ $viewMode: 'grid' | 'list' }>`
  display: grid;
  grid-template-columns: ${props => props.$viewMode === 'grid' 
    ? 'repeat(auto-fit, minmax(300px, 1fr))' 
    : '1fr'};
  gap: 1.5rem;
  width: 100%;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const ProductCard = styled(motion.div)<{ $viewMode: 'grid' | 'list' }>`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  display: ${props => props.$viewMode === 'list' ? 'flex' : 'block'};
  border: 1px solid #f1f5f9;
  position: relative;
  z-index: 1;
  
  &:hover {
    box-shadow: 0 10px 25px -3px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
    border-color: #e2e8f0;
  }
  
  @media (max-width: 768px) {
    border-radius: 12px;
    box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.1);
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 8px -1px rgba(0, 0, 0, 0.15);
    }
  }
`;

const ProductImage = styled.div<{ $viewMode: 'grid' | 'list' }>`
  height: ${props => props.$viewMode === 'grid' ? '200px' : '120px'};
  width: ${props => props.$viewMode === 'list' ? '150px' : '100%'};
  overflow: hidden;
  position: relative;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  flex-shrink: 0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: transform 0.3s ease;
    border-radius: 8px;
  }
  
  ${ProductCard}:hover & img {
    transform: scale(1.05);
  }
  
  @media (max-width: 768px) {
    height: ${props => props.$viewMode === 'grid' ? '180px' : '100px'};
    width: ${props => props.$viewMode === 'list' ? '120px' : '100%'};
    padding: 0.75rem;
  }
`;

const ProductContent = styled.div<{ $viewMode: 'grid' | 'list' }>`
  padding: ${props => props.$viewMode === 'grid' ? '1.25rem' : '1rem'};
  flex: 1;
  
  @media (max-width: 768px) {
    padding: ${props => props.$viewMode === 'grid' ? '1rem' : '0.75rem'};
  }
`;

const ProductCategory = styled.span<{ $isRTL: boolean }>`
  display: inline-block;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  @media (max-width: 768px) {
    font-size: 0.7rem;
    padding: 0.2rem 0.6rem;
    margin-bottom: 0.5rem;
  }
`;

const ProductTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;
  line-height: 1.4;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 0.4rem;
  }
`;

const ProductDescription = styled.p<{ $isRTL: boolean }>`
  color: #64748b;
  line-height: 1.5;
  margin-bottom: 1rem;
  display: -webkit-box;
  -webkit-line-clamp: ${props => props.$isRTL ? '2' : '3'};
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 0.9rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
    margin-bottom: 0.75rem;
    -webkit-line-clamp: 2;
  }
`;

const ProductButton = styled.button<{ $isRTL: boolean }>`
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
  border: none;
  padding: 0.6rem 1.25rem;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.85rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  width: 100%;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
`;

const LoadingText = styled.p<{ $isRTL: boolean }>`
  font-size: 1.1rem;
  color: #6b7280;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const EmptyState = styled.div<{ $isRTL: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 40vh;
  text-align: center;
  padding: 2rem;
`;

const EmptyStateIcon = styled.div`
  font-size: 4rem;
  color: #cbd5e1;
  margin-bottom: 1rem;
`;

const EmptyStateTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.5rem;
  font-weight: 700;
  color: #374151;
  margin-bottom: 0.5rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const EmptyStateText = styled.p<{ $isRTL: boolean }>`
  font-size: 1rem;
  color: #6b7280;
  margin-bottom: 1.5rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const AllProducts: React.FC<AllProductsProps> = ({ 
  t, 
  isRTL, 
  language, 
  setLanguage,
  searchTerm,
  setSearchTerm,
  selectedCategory,
  setSelectedCategory,
  viewMode,
  setViewMode,
  onClearFilters,
  hasActiveFilters
}) => {
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadProducts();
  }, []);
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await FirebaseService.getAll('products');
      setProducts(data || []);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProductClick = (productId: string) => {
    navigate(`/product/${productId}`);
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = searchTerm === '' || 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (Array.isArray(product.description) 
        ? product.description.join(' ').toLowerCase().includes(searchTerm.toLowerCase())
        : product.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === '' || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <AllProductsPage $isRTL={isRTL}>
        <LoadingContainer>
          <LoadingText $isRTL={isRTL}>{t.loading || 'Chargement des produits...'}</LoadingText>
        </LoadingContainer>
      </AllProductsPage>
    );
  }

  return (
    <AllProductsPage $isRTL={isRTL}>
      <ProductsSection>
        <ProductsContainer>
          <ResultsHeader $isRTL={isRTL}>
            <ResultsCount $isRTL={isRTL}>
              {filteredProducts.length} {t.productsFound || 'produits trouvés'}
            </ResultsCount>
          </ResultsHeader>
          
          {filteredProducts.length === 0 ? (
            <EmptyState $isRTL={isRTL}>
              <EmptyStateIcon>���</EmptyStateIcon>
              <EmptyStateTitle $isRTL={isRTL}>
                {t.noProductsFound || 'Aucun produit trouvé'}
              </EmptyStateTitle>
              <EmptyStateText $isRTL={isRTL}>
                {t.noProductsFoundText || 'Essayez de modifier vos critères de recherche ou de filtrage'}
              </EmptyStateText>
              {hasActiveFilters && (
                <ProductButton $isRTL={isRTL} onClick={onClearFilters}>
                  ✕ {t.clearFilters || 'Effacer les filtres'}
                </ProductButton>
              )}
            </EmptyState>
          ) : (
            <ProductsGrid $viewMode={viewMode}>
              {filteredProducts.map((product, index) => (
                <ProductCard
                  key={product.id}
                  $viewMode={viewMode}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  onClick={() => handleProductClick(product.id)}
                >
                  <ProductImage $viewMode={viewMode}>
                    <img
                      src={product.mainImage || '/images/products/default-product.jpg'}
                      alt={product.name}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = '/images/products/default-product.jpg';
                      }}
                    />
                  </ProductImage>
                  <ProductContent $viewMode={viewMode}>
                    <ProductCategory $isRTL={isRTL}>
                      {product.category === '0' ? t.materialEquipment : 
                       product.category === '1' ? t.groupesElectrogenes : 
                       product.category === '2' ? t.accessoriesElectriques : product.category}
                    </ProductCategory>
                    <ProductTitle $isRTL={isRTL}>
                      {product.name}
                    </ProductTitle>
                    <ProductDescription $isRTL={isRTL}>
                      {Array.isArray(product.description) 
                        ? product.description.join(' ') 
                        : product.description}
                    </ProductDescription>
                    <ProductButton $isRTL={isRTL}>
                      {t.viewDetails || 'Voir les détails'}
                    </ProductButton>
                  </ProductContent>
                </ProductCard>
              ))}
            </ProductsGrid>
          )}
        </ProductsContainer>
      </ProductsSection>
    </AllProductsPage>
  );
};

export default AllProducts;
